<script setup lang="ts">
import { computed } from "vue";
import type { RouteLocationRaw } from "vue-router";

import { getIconClasses } from "@/helpers/cssClass";
import { isset } from "@/helpers/object";

import type { IconObject } from "@/dsComponents/buttons/types";

import Loader from "@/dsComponents/Loader.vue";

interface ButtonAltProps {
    type?: "primary" | "neutral" | "error";
    variant?: "ghost" | "stroke" | "filled";
    size?: "sm" | "md" | "xs";

    disabled?: boolean;
    active?: boolean;
    alert?: boolean;
    readonly?: boolean;

    fullWidth?: boolean;
    fixedWidth?: string;
    label?: string;
    leftIcon?: string | IconObject;
    leftIconColor?: string;
    rightIcon?: string | IconObject;
    rightIconColor?: string;
    iconOnly?: string | IconObject;
    loading?: boolean;
    shadow?: boolean;
    to?: RouteLocationRaw;
}

const props = withDefaults(defineProps<ButtonAltProps>(), {
    type: "primary",
    variant: "ghost",
    size: "md",
    shadow: false,
});

const emit = defineEmits<(e: "click", event: MouseEvent) => void>();

const classes = computed(() => [
    "ds-button-alt",
    props.type,
    props.variant,
    props.size,
    {
        "icon-only": props.iconOnly,
        disabled: props.loading || props.disabled,
        active: props.active,
        alert: props.alert,
        fullWidth: props.fullWidth,
        "left-icon": isset(props.leftIcon),
        shadow: props.shadow,
    },
]);

const LABEL_TYPO_BY_SIZE: Record<NonNullable<ButtonAltProps["size"]>, string> = {
    md: "typo-sm-medium",
    sm: "typo-xs-medium",
    xs: "typo-xxs-medium",
};

const labelTypoClass = computed(() => LABEL_TYPO_BY_SIZE[props.size]);
</script>

<template>
    <component
        :is="to ? 'router-link' : 'button'"
        :to="to"
        :class="classes"
        :style="fixedWidth ? { width: fixedWidth, minWidth: fixedWidth } : undefined"
        :disabled="disabled || loading || readonly"
        @click="emit('click', $event)"
    >
        <template v-if="!iconOnly">
            <i v-if="leftIcon" :class="['left-icon', ...getIconClasses(leftIcon)]" :style="leftIconColor ? { color: leftIconColor } : undefined" />
            <div v-if="loading" class="loader">
                <Loader />
            </div>
            <span :class="['btn-alt-label', labelTypoClass]">
                <slot>{{ label ?? "" }}</slot>
            </span>

            <i
                v-if="rightIcon"
                :class="['right-icon', ...getIconClasses(rightIcon)]"
                :style="rightIconColor ? { color: rightIconColor } : undefined"
            />
        </template>

        <template v-else class="icon-only">
            <div v-if="loading" class="loader">
                <Loader />
            </div>
            <i v-else :class="['icon-only', ...getIconClasses(iconOnly)]" />
        </template>
    </component>
</template>

<style lang="scss" scoped>
@mixin filled($bg, $bg-hover) {
    background-color: $bg;
    &:not(.disabled) {
        color: var(--text-white-0, #ffffff);
    }
    &:hover:not(.disabled),
    &:focus-visible:not(.disabled) {
        color: var(--text-white-0, #ffffff);
        background-color: $bg-hover;
    }
    &.disabled {
        background-color: var(--bg-weak-100, #eff2f5);
    }
}

.ds-button-alt {
    display: flex;
    gap: var(--spacing-xs, 4px);
    align-items: center;
    padding: 5px var(--spacing-md, 8px);
    border-radius: var(--radius-lg, 10px);
    position: relative;
    width: fit-content;
    background-color: var(--bg-white-0, #ffffff);
    justify-content: center;
    height: 36px;

    &:disabled {
        cursor: default;
        pointer-events: none;
    }

    &.sm {
        height: 28px;
        border-radius: var(--radius-md, 8px);
    }

    &.xs {
        height: 20px;
        padding: 0 var(--spacing-xs, 4px);
        border-radius: var(--radius-sm, 6px);

        .btn-alt-label {
            padding: 0;
        }
    }

    &.ghost {
        background-color: transparent;
    }

    &.stroke {
        &.neutral {
            border: 1px solid var(--stroke-soft-200, #dbdfe6);
            &.disabled {
                background-color: var(--bg-weak-100, #eff2f5);
            }
        }
        &.primary {
            border: 1px solid var(--blue-light, #d6dfff);
        }
        &.error {
            border: 1px solid var(--red-light, #fbdae0);
        }
    }

    &.filled.neutral {
        @include filled(var(--bg-surface-700, #323848), var(--bg-strong-900, #00061a));
    }

    &.filled.primary {
        @include filled(var(--blue-base, #2051f8), var(--blue-dark, #042aaf));
    }

    &.filled.error {
        @include filled(var(--red-base, #e51940), var(--red-dark, #8a0f27));
    }

    .btn-alt-label {
        margin: 0;
        padding: 0 var(--spacing-xs, 4px);
    }

    i {
        width: 18px;
        height: 18px;
        font-size: 18px;
    }

    &.sm i {
        width: 14px;
        height: 14px;
        font-size: 14px;
    }

    &.xs i {
        width: 12px;
        height: 12px;
        font-size: 12px;
    }

    .loader {
        flex-grow: 0;
    }

    &.primary {
        color: var(--blue-base, #2051f8);
        &:hover:not(.disabled),
        &:focus-visible:not(.disabled) {
            background-color: var(--bg-weak-100, #eff2f5);
        }
    }

    &.neutral {
        color: var(--neutral-700, #323848);
        &:hover:not(.disabled),
        &:focus-visible:not(.disabled) {
            background-color: var(--bg-weak-100, #eff2f5);
            color: var(--text-main-900, #00061a);
        }
    }

    &.error {
        color: var(--red-base, #e51940);
        &:hover:not(.disabled),
        &:focus-visible:not(.disabled) {
            background-color: var(--red-lighter, #fbdae0);
        }
    }

    &:not(.disabled) {
        &.active,
        &.alert {
            &::before {
                position: absolute;
                content: "";
                height: 6px;
                width: 6px;
                border-radius: 100%;
                right: 5px;
                top: 5px;
            }
        }
        &.left-icon::before {
            left: 17px;
            top: 5px;
        }
        &.icon-only::before {
            right: 5px;
            top: 5px;
        }
    }

    &.active::before {
        background-color: var(--blue-base, #2051f8);
    }

    &.alert::before {
        background-color: var(--red-base, #e51940);
    }

    &.disabled {
        color: var(--text-disabled-300, #acb1c3);
        cursor: default;
    }

    &.icon-only {
        justify-content: center;
        width: 36px;
        padding: 0;

        &.sm {
            width: 28px;
        }

        &.xs {
            width: 20px;
        }
    }

    &.fullWidth {
        width: 100%;
    }

    &.shadow {
        box-shadow: var(--regular-shadow-xs, 0 1px 2px 0 #dbdfe640);
    }
}
</style>
